/**
 * JIRA/HubSpot Style Kanban Column
 * Professional column with collapsible functionality and stage indicators
 */
import React, { useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { ChevronDown, ChevronRight, Plus, MoreHorizontal } from 'lucide-react';
import { formatCompactCurrency } from '../../api/pipeline';
import KanbanCard from './KanbanCard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Stage color mapping for header indicator
const STAGE_COLORS = {
  target: { bg: 'bg-blue-500', light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  qualified: { bg: 'bg-purple-500', light: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  rfq: { bg: 'bg-indigo-500', light: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700' },
  quoted: { bg: 'bg-cyan-500', light: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
  awarded: { bg: 'bg-emerald-500', light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  sourced: { bg: 'bg-green-500', light: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  lost: { bg: 'bg-slate-400', light: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700' },
};

const KanbanColumn = ({
  stage,
  opportunities,
  stats,
  onCardClick,
  onStageChange,
  onArchive,
  onAddOpportunity,
  onOpenPlanning,
  onOpenProgram,
  workflowCounts = {},
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const colors = STAGE_COLORS[stage.id] || STAGE_COLORS.target;
  
  const count = stats?.count || opportunities.length;
  const revenue = stats?.revenue || 0;

  return (
    <div 
      className={`
        flex-shrink-0 flex flex-col bg-slate-50 rounded-lg border border-slate-200
        transition-all duration-200
        ${isCollapsed ? 'w-12' : 'w-80'}
      `}
    >
      {/* Column Header */}
      <div 
        className={`
          sticky top-0 z-10 rounded-t-lg border-b border-slate-200
          ${colors.light}
        `}
      >
        {/* Color indicator bar */}
        <div className={`h-1 ${colors.bg} rounded-t-lg`} />
        
        {isCollapsed ? (
          /* Collapsed Header */
          <button 
            onClick={() => setIsCollapsed(false)}
            className="w-full p-3 text-center hover:bg-white/50 transition-colors"
          >
            <div className="flex flex-col items-center gap-2">
              <ChevronRight className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-semibold text-slate-700 [writing-mode:vertical-lr] rotate-180">
                {stage.name}
              </span>
              <span className={`
                inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
                text-xs font-semibold rounded-full
                ${colors.bg} text-white
              `}>
                {count}
              </span>
            </div>
          </button>
        ) : (
          /* Expanded Header */
          <div className="p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setIsCollapsed(true)}
                  className="p-0.5 rounded hover:bg-slate-200/50 transition-colors"
                >
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </button>
                <h3 className="text-sm font-semibold text-slate-900">
                  {stage.name}
                </h3>
                <span className={`
                  inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
                  text-xs font-semibold rounded-full
                  ${colors.bg} text-white
                `}>
                  {count}
                </span>
              </div>
              
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => onAddOpportunity?.(stage.id)}
                  className="p-1 rounded hover:bg-slate-200/50 text-slate-500 hover:text-slate-700 transition-colors"
                  title="Add opportunity"
                >
                  <Plus className="w-4 h-4" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded hover:bg-slate-200/50 text-slate-500 hover:text-slate-700 transition-colors">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setIsCollapsed(true)}>
                      Collapse column
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Revenue total */}
            <p className="text-xs text-slate-600 ml-6">
              {formatCompactCurrency(revenue)}
            </p>
          </div>
        )}
      </div>

      {/* Cards Container */}
      {!isCollapsed && (
        <Droppable droppableId={stage.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`
                flex-1 overflow-y-auto p-2 min-h-[200px]
                transition-colors duration-150
                ${snapshot.isDraggingOver 
                  ? 'bg-blue-50 ring-2 ring-inset ring-blue-300 ring-dashed' 
                  : ''}
              `}
            >
              {opportunities.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <p className="text-xs">No opportunities</p>
                  <button 
                    onClick={() => onAddOpportunity?.(stage.id)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Add one
                  </button>
                </div>
              ) : (
                opportunities.map((opp, index) => (
                  <Draggable
                    key={opp.id}
                    draggableId={opp.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <KanbanCard
                          opportunity={opp}
                          onClick={() => onCardClick(opp)}
                          isDragging={snapshot.isDragging}
                          onStageChange={onStageChange}
                          onArchive={onArchive}
                          onOpenPlanning={onOpenPlanning}
                          onOpenProgram={onOpenProgram}
                          activeWorkflowsCount={workflowCounts[opp.id] || 0}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  );
};

export default KanbanColumn;
